import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import * as Yup from "yup";

import {
  maxLtContractDemandLoad,
  minHtContractDemandLoad,
  regexForName,
} from "common/constant";
import { getConsumerNumberValidation } from "utils/getConsumerNumberValidation";
import { getDocumentsValidationSchema } from "utils/getDocumentsValidationSchema";

import { useApplicantTypeQuery } from "apis/ApplicantType";
import { usePremiseTypeQuery } from "apis/PremiseType";

const premisesForHP = ["temporary"];

export const useValidationSchemaForDemandAlteration = (
  isDemandExtension = false
) => {
  const [premiseIdsForHP, setPremiseIdsForHP] = useState([]);

  const formData = useSelector((state) => state.demandAlteration);

  const { data: { data: premiseType } = {} } = usePremiseTypeQuery({
    utilityId: 1,
  });
  const { data: { data: { items: applicantType = [] } = {} } = {} } =
    useApplicantTypeQuery();

  useEffect(() => {
    if (premiseType) {
      const updatedPremiseIds = [];
      premiseType.forEach((premise) => {
        if (premisesForHP.includes(premise.name.toLowerCase())) {
          updatedPremiseIds.push(premise.id);
        }
      });
      setPremiseIdsForHP(updatedPremiseIds);
    }
  }, [premiseType]);
  const validationSchema = useMemo(
    () => [
      Yup.object({
        utilityConsumerId: Yup.number().required("Customer number is required"),
        applicant: Yup.object({
           panNumber: Yup.string()
                      .matches(
                        /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/,
                        "Please enter a valid Pan number"
                      ).required("Pan number is required"),
          isGSTRegistered: Yup.string().required("GST is required"),
          gstNumber: Yup.string()
            .test("isGstRequired", "", function (_, context) {
              const [{ value: { isGSTRegistered, gstNumber } } = {}] =
                context.from || [];
              if (isGSTRegistered && !gstNumber) {
                return context.createError({
                  message: "GST number is required",
                });
              }
              return true;
            })
            .matches(
              /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz][0-9A-Za-z]{1}$/,
              "Please enter a valid GST number"
            ),
        }),
        connectionLoad: Yup.object({
          loadDetails: Yup.string().required("Load details is required"),
          supplyVoltage: Yup.string().required("Supply voltage is required"),
          tention: Yup.string().required("Please select tention"),

          increaseOfLoad: isDemandExtension
            ? formData.connectionLoad.tention === "LT"
              ? premiseIdsForHP.includes(
                  formData.extraValues.premiseTypeId || 0
                )
                ? Yup.number()
                    .required("Increase of load is required")
                    .test("isValidIncreaseOfLoadLT", "", function () {
                      const [
                        { value: current = null } = {},
                        { value: { payableBillAmount } = null } = {},
                      ] = this.from || [];
                      if (payableBillAmount <= 0) {
                        if (current.increaseOfLoad <= 0) {
                          return this.createError({
                            message: "Contract load must be positive",
                          });
                        }
                        if (
                          current.increaseOfLoad >=
                          125 - formData.connectionLoad.powerRequirement
                        ) {
                          return this.createError({
                            message:
                              "Invalid value, Please change power requirement to HT",
                          });
                        }
                      }
                      return true;
                    })
                : Yup.number()
                    .required("Increase of load is required")
                    .test("isValidIncreaseOfLoadLT", "", function () {
                      const [
                        { value: current = null } = {},
                        { value: { payableBillAmount } = null } = {},
                      ] = this.from || [];
                      if (payableBillAmount <= 0) {
                        if (current.increaseOfLoad <= 0) {
                          return this.createError({
                            message: "Contract load must be positive",
                          });
                        }
                        if (
                          current.increaseOfLoad >
                          maxLtContractDemandLoad -
                            formData.connectionLoad.powerRequirement
                        ) {
                          return this.createError({
                            message:
                              "Invalid value, Please change power requirement to HT",
                          });
                        }
                      }
                      return true;
                    })
              : premiseIdsForHP.includes(
                  formData.extraValues.premiseTypeId || 0
                )
              ? Yup.number()
                  .required("Increase of load is required")

                  .test("isValidIncreaseOfLoadHT", "", function () {
                    const [
                      { value: current = null } = {},
                      { value: { payableBillAmount } = null } = {},
                    ] = this.from || [];
                    if (payableBillAmount <= 0) {
                      if (current.increaseOfLoad <= 0) {
                        return this.createError({
                          message: "Contract demand must be positive",
                        });
                      }
                      if (
                        current.increaseOfLoad >
                        125 - formData.connectionLoad.powerRequirement
                      ) {
                        return this.createError({
                          message:
                            "Enter a value that makes the total increase of load more than 125",
                        });
                      }
                      if (
                        current.increaseOfLoad >
                        100000 - formData.connectionLoad.powerRequirement
                      ) {
                        return this.createError({
                          message: `Maximum value allowed for increase of load is ${
                            99999 - formData.connectionLoad.powerRequirement
                          }`,
                        });
                      }
                    }
                    return true;
                  })
              : Yup.number()
                  .required("Increase of load is required")
                  .test("isValidIncreaseOfLoadHT", "", function () {
                    const [
                      { value: current = null } = {},
                      { value: { payableBillAmount } = null } = {},
                    ] = this.from || [];
                    if (payableBillAmount <= 0) {
                      if (current.increaseOfLoad <= 0) {
                        return this.createError({
                          message: "Increase of load must be positive",
                        });
                      }
                      if (
                        current.increaseOfLoad <
                        minHtContractDemandLoad -
                          formData.connectionLoad.powerRequirement
                      ) {
                        return this.createError({
                          message:
                            "Enter a value that makes the total power requirement more than 150",
                        });
                      }
                      if (
                        current.increaseOfLoad >
                        100000 - formData.connectionLoad.powerRequirement
                      ) {
                        return this.createError({
                          message: `Maximum value allowed for contract demand is ${
                            99999 - formData.connectionLoad.powerRequirement
                          }`,
                        });
                      }
                    }
                    return true;
                  })
            : Yup.number(),

          decreaseOfLoad: isDemandExtension
            ? Yup.number()
            : formData.connectionLoad.tention === "LT"
            ? premiseIdsForHP.includes(formData.extraValues.premiseTypeId || 0)
              ? Yup.number()
                  .required("Decrease of load is required")
                  .test("isValidDecreaseOfLoadLT", "", function () {
                    const [
                      { value: current = null } = {},
                      { value: { payableBillAmount } = null } = {},
                    ] = this.from || [];
                    if (payableBillAmount <= 0) {
                      if (current.decreaseOfLoad <= 0) {
                        return this.createError({
                          message: "Decrease of load must be positive",
                        });
                      }
                      if (
                        current.decreaseOfLoad <=
                        formData.connectionLoad.powerRequirement
                      ) {
                        return this.createError({
                          message: "Value should be less then existing load",
                        });
                      }
                      if (
                        current.decreaseOfLoad >=
                        formData.connectionLoad.powerRequirement - 126
                      ) {
                        return this.createError({
                          message:
                            "Invalid value, Please change power requirement to HT",
                        });
                      }
                    }
                    return true;
                  })
              : Yup.number()
                  .required("Decrease of load is required")
                  .test("isValidDecreaseOfLoadLT", "", function () {
                    const [
                      { value: current = null } = {},
                      { value: { payableBillAmount } = null } = {},
                    ] = this.from || [];
                    if (payableBillAmount <= 0) {
                      if (current.decreaseOfLoad <= 0) {
                        return this.createError({
                          message: "Decrease of load must be positive",
                        });
                      }
                      if (
                        current.decreaseOfLoad >=
                        formData.connectionLoad.powerRequirement
                      ) {
                        return this.createError({
                          message: "Value should be less then existing load",
                        });
                      }
                      if (
                        current.decreaseOfLoad <=
                        formData.connectionLoad.powerRequirement -
                          minHtContractDemandLoad
                      ) {
                        return this.createError({
                          message:
                            "Invalid value, Please change power requirement to HT",
                        });
                      }
                    }
                    return true;
                  })
            : premiseIdsForHP.includes(formData.extraValues.premiseTypeId || 0)
            ? Yup.number()
                .required("Decrease of load is required")
                .positive("Decrease of load value must be positive")
                .test({
                  name: "checkIsValid",
                  test: (value) =>
                    value < formData.connectionLoad.powerRequirement - 125,
                  message:
                    "Enter a value that makes the total power requirement more than 125",
                })

                .test("isValidDecreaseOfLoadHT", "", function () {
                  const [
                    { value: current = null } = {},
                    { value: { payableBillAmount } = null } = {},
                  ] = this.from || [];
                  if (payableBillAmount <= 0) {
                    if (current.decreaseOfLoad <= 0) {
                      return this.createError({
                        message: "Decrease of load must be positive",
                      });
                    }
                    if (
                      current.decreaseOfLoad >
                      formData.connectionLoad.powerRequirement - 125
                    ) {
                      return this.createError({
                        message:
                          "Enter a value that makes the total power requirement more than 125",
                      });
                    }
                  }
                  return true;
                })
            : Yup.number()
                .required("Decrease of load is required")
                .test("isValidDecreaseOfLoadHT", "", function () {
                  const [
                    { value: current = null } = {},
                    { value: { payableBillAmount } = null } = {},
                  ] = this.from || [];
                  if (payableBillAmount <= 0) {
                    if (current.decreaseOfLoad <= 0) {
                      return this.createError({
                        message: "Decrease of load must be positive",
                      });
                    }
                    if (
                      current.decreaseOfLoad >=
                      formData.connectionLoad.powerRequirement -
                        maxLtContractDemandLoad
                    ) {
                      return this.createError({
                        message:
                          "Enter a value that makes the total power requirement more than 150",
                      });
                    }
                  }
                  return true;
                }),
        }),
      }),

      Yup.object({
        authorizedPerson: Yup.object({
          salutation: Yup.string().required("Salutation is required"),
          firstName: Yup.string()
            .matches(regexForName, "Please enter a valid first name")
            .required("First name is required"),
          middleName: Yup.string().matches(
            regexForName,
            "Please enter a valid middle name"
          ),
          lastName: Yup.string().matches(
            regexForName,
            "Please enter a valid last name"
          ),
          // gender: Yup.string().required("Gender is required"),
          // ageCategory: Yup.string().required("Age category is required"),
          email: Yup.string()
            .email("Please enter valid email")
            .required("Email is required"),
          mobileNumber: Yup.string()
            .required("Mobile number is required")
            .matches(/^\d{10}$/, "Please enter valid mobile number"),
          landLine: Yup.string().matches(
            /^\d{8,11}$/,
            "Please enter a valid landline number"
          ),
          imageUrl: Yup.string().test(
            "imageRequired",
            "Please upload image",
            function () {
              const [{ value: current = null } = {}] = this.from || [];
              return !!(current?.image || current?.imageUrl);
            }
          ),
        }),
      }),

      Yup.object({
        documents: getDocumentsValidationSchema(),
      }),
      Yup.object({
        declaration: Yup.object({
          isConsumerDuesOutStanding: Yup.boolean(),
          consumerDuesOutstanding: Yup.string().when(
            "isConsumerDuesOutstanding",
            {
              is: true,
              then: (schema) => getConsumerNumberValidation(schema),
              otherwise: (schema) => schema,
            }
          ),
          isPremisesDuesOutstanding: Yup.boolean(),
          premisesDuesOutstanding: Yup.string().when(
            "isPremisesDuesOutstanding",
            {
              is: true,
              then: (schema) => getConsumerNumberValidation(schema),
              otherwise: (schema) => schema,
            }
          ),
          isFirmDuesOutstanding: Yup.boolean(),
          firmDuesOutstanding: Yup.string().when("isFirmDuesOutstanding", {
            is: true,
            then: (schema) => getConsumerNumberValidation(schema),

            otherwise: (schema) => schema,
          }),
        }),
      }),
    ],
    [
      formData.extraValues.premiseTypeId,
      formData.connectionLoad.tention,
      formData.connectionLoad.powerRequirement,
      premiseIdsForHP,
      applicantType,
    ]
  );

  const useValidationSchemaForDemandAlteration = useCallback(
    (index) => validationSchema[index],
    [validationSchema]
  );
  return useValidationSchemaForDemandAlteration;
};
